import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { useTasks } from "@/lib/hooks/useTasks";
import { usePanelStore } from "@/state/stores";
import {
  CheckCircle2,
  Clock,
  Loader2,
  PanelBottomCloseIcon,
} from "lucide-react";
import type { AgentTask } from "../../../../../schemas/codegen";

interface TaskListItemProps {
  task: AgentTask;
  taskNumber: number;
  totalTasks: number;
}

function TaskListItem({ task, taskNumber, totalTasks }: TaskListItemProps) {
  const getStatusIcon = () => {
    if (task.status === "in_progress") {
      return <Loader2 className="h-3 w-3 animate-spin text-blue-400" />;
    }
    if (task.status === "pending") {
      return <Clock className="h-3 w-3 text-neutral-500" />;
    }
    if (task.status === "completed") {
      return <CheckCircle2 className="h-3 w-3 text-green-400" />;
    }
    return null;
  };

  return (
    <div className="grid grid-cols-[auto_auto_1fr] gap-3 px-4 items-center">
      <span className="text-xs text-neutral-500 font-mono">
        {taskNumber}/{totalTasks}
      </span>

      <div className="flex justify-center items-center">{getStatusIcon()}</div>

      <div className="min-w-0">
        <div className="text-sm text-neutral-300">{task.title}</div>
        {task.description && (
          <div className="text-xs text-neutral-500 mt-0.5">
            {task.description}
          </div>
        )}
      </div>
    </div>
  );
}

interface TaskListProps {
  tasks: AgentTask[];
}

function TaskList({ tasks }: TaskListProps) {
  // Sort tasks by sort order to maintain creation order
  const sortedTasks = [...tasks].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="grid gap-2 py-3">
      {sortedTasks.map((task, index) => (
        <TaskListItem
          key={task.id}
          task={task}
          taskNumber={index + 1}
          totalTasks={tasks.length}
        />
      ))}
    </div>
  );
}

export function TasksTab() {
  const { activeTab, togglePanel } = usePanelStore();
  const activeChatId = useActiveChatId();

  // Fetch tasks for the current chat
  const { data: tasks = [], isLoading } = useTasks(activeChatId);

  const renderContent = () => {
    switch (activeTab) {
      case "tasks": {
        return (
          <>
            {/* Title Bar */}
            <header className="flex items-center justify-between bg-neutral-800 py-2 px-4 rounded-t-xl">
              <h3 className="text-sm flex items-center gap-1.5">Agent Tasks</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={togglePanel}
                className="h-6 w-6 hover:bg-neutral-700/80"
              >
                <PanelBottomCloseIcon className="w-4 h-4" />
              </Button>
            </header>

            {/* Content Section */}
            <main>
              {isLoading ? (
                <div className="px-4 py-3 space-y-2">
                  <Skeleton className="h-16 w-full bg-neutral-800" />
                  <Skeleton className="h-16 w-full bg-neutral-800" />
                  <Skeleton className="h-16 w-full bg-neutral-800" />
                </div>
              ) : tasks.length === 0 ? (
                <div className="px-4 py-8 text-center text-neutral-400">
                  <p className="text-sm">No tasks yet</p>
                  <p className="text-xs mt-1">
                    The agent will create tasks for complex workflows
                  </p>
                </div>
              ) : (
                <TaskList tasks={tasks} />
              )}
            </main>
          </>
        );
      }
      default:
        return null;
    }
  };

  return <>{renderContent()}</>;
}
